import type { PlanningCycle } from '../types/planning';

/**
 * Decode the 5 HTML entities that Python's html.escape(quote=True) produces.
 * The backend sanitises all incoming JSON strings at the API boundary (XSS
 * prevention). This reverses that encoding at render time so users see plain
 * text, not entity sequences like &#x27; or &amp;.
 *
 * Some records can contain repeated escaping from legacy save paths (for
 * example "&amp;amp;"). Decode in bounded passes so deeply nested entities
 * render correctly without risking unbounded loops.
 */
export function decodeHtml(str: string | null | undefined): string {
  if (!str) return str ?? '';
  let decoded = str;
  const MAX_PASSES = 5;
  for (let i = 0; i < MAX_PASSES; i += 1) {
    const next = decoded
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'");
    if (next === decoded) break;
    decoded = next;
  }
  return decoded;
}

/** Short cycle label for selectors (e.g. BP27, FRE26). */
export function planningCycleShortLabel(cycle: Pick<PlanningCycle, 'cycle_type' | 'fiscal_year'>): string {
  const fy = cycle.fiscal_year;
  const suffix = fy >= 2000 ? String(fy).slice(-2) : String(fy);
  return `${cycle.cycle_type}${suffix}`;
}

/**
 * Default Bridges Base → Compare axis selection, derived from the selected
 * working cycle:
 *
 * - Working cycle is BP{Y}       → compare = BP{Y},          base = BP{Y-1}.
 * - Working cycle is FRE/SRE{Y}  → compare = the working cycle, base = BP{Y}.
 *
 * When the expected base cycle is missing (draft-only or absent), fall back to
 * the latest consecutive BP pair. This only produces a default — the user can
 * always override the axes on the planning page.
 */
export function getDefaultBridgeCycleSelection(
  cycles: PlanningCycle[],
  selectedCycle?: PlanningCycle | null,
): { baseCycleId: number | null; compareCycleId: number | null } {
  const viewable = cycles.filter((c) => c.status !== 'draft');
  if (viewable.length === 0) {
    return { baseCycleId: null, compareCycleId: null };
  }

  const bpCycles = viewable.filter((c) => c.cycle_type === 'BP');
  const findBp = (fy: number) => bpCycles.find((c) => c.fiscal_year === fy) ?? null;

  const working = selectedCycle
    ? viewable.find((c) => c.id === selectedCycle.id) ?? null
    : null;

  if (working) {
    if (working.cycle_type === 'BP') {
      const priorBp = findBp(working.fiscal_year - 1);
      if (priorBp && priorBp.id !== working.id) {
        return { baseCycleId: priorBp.id, compareCycleId: working.id };
      }
    } else {
      // FRE / SRE → base is the same-year BP, compare is the working cycle.
      const sameYearBp = findBp(working.fiscal_year);
      if (sameYearBp && sameYearBp.id !== working.id) {
        return { baseCycleId: sameYearBp.id, compareCycleId: working.id };
      }
    }
    // Expected base unavailable — fall through to the generic BP fallback below.
  }

  const currentBp =
    bpCycles.find((c) => c.status === 'open') ??
    [...bpCycles].sort((a, b) => b.fiscal_year - a.fiscal_year)[0];

  if (!currentBp) {
    if (viewable.length >= 2) {
      return { baseCycleId: viewable[1].id, compareCycleId: viewable[0].id };
    }
    return { baseCycleId: viewable[0].id, compareCycleId: null };
  }

  const priorBp = findBp(currentBp.fiscal_year - 1);
  if (priorBp && priorBp.id !== currentBp.id) {
    return { baseCycleId: priorBp.id, compareCycleId: currentBp.id };
  }

  return { baseCycleId: currentBp.id, compareCycleId: null };
}

/** Long subtitle for IO Plans (legacy-style). */
export function planningCycleLongSubtitle(cycle: Pick<PlanningCycle, 'cycle_type' | 'fiscal_year' | 'status'>): string {
  const typeNames: Record<string, string> = {
    BP: 'Budget Plan',
    FRE: 'First Re-Estimate',
    SRE: 'Second Re-Estimate',
  };
  const name = typeNames[cycle.cycle_type] ?? cycle.cycle_type;
  return `${name} FY${cycle.fiscal_year} · ${cycle.status}`;
}
